/**
 * Professionell PDF-generator för fordonstest.
 * BEA | Testprotokoll med gul rand, 2 bilder per rad, sidnummer, snygga sidbryt, alla sektioner.
 */
import jsPDF from "jspdf";
import { VehicleTest, ExteriorImage, DamageImage, is360ExteriorFrame } from "@/types/vehicleTest";
import type { VehicleDetails } from "@/types/vehicleDetails";
import { FileObject } from "@/types/shared/file";
import { formatMileage } from "./listingUtils";
import { fuelTypeLabels, transmissionTypeLabels } from "@/i18n/labels/vehicleLabels";
import { TransmissionType, VehicleFuelType } from "@/types/enums";
import { carTestLabels, valuationLabels, getEquipmentLabel } from "@/i18n/labels";
import { getLanguage, type Language } from "@/utils/languageUtils";

const MARGIN = 18;
const YELLOW_BAR_HEIGHT = 14;
const YELLOW_BAR_PADDING_BOTTOM = 5;
const HEADER_HEIGHT = 14;
const FOOTER_HEIGHT = 12;
const LINE_HEIGHT = 5.5;
const SECTION_GAP = 4;
const CARD_PADDING = 2;
const CARD_PADDING_BOTTOM = 0.5;
const CARD_RADIUS = 2.5;
const CARD_GAP = 1;
const SUB_SECTION_GAP = 1;

/** Mappa kundtyp/roll (t.ex. dealer, individual, business) till önskat språk. */
function formatCustomerTypePdf(role?: string, lang?: Language): string {
  const language: Language = lang ?? getLanguage();
  const ct = carTestLabels(language);
  if (!role) return ct.pdf.values.notSpecified;
  const lower = role.toLowerCase();
  const customerTypes = ct.pdf.values.customerTypes;
  return customerTypes[lower as keyof typeof customerTypes] ?? role;
}

/** Mappa växellåda (t.ex. MANUAL) till önskat språk. */
function formatTransmissionPdf(transmission?: string, lang?: Language): string {
  const language: Language = lang ?? getLanguage();
  const labels = transmissionTypeLabels(language);
  if (!transmission) {
    const ct = carTestLabels(language);
    return ct.pdf.values.notSpecifiedShort;
  }
  const upper = transmission.toUpperCase().replace(/-/g, "_");
  if (upper in TransmissionType) {
    const v = labels[upper as TransmissionType];
    if (v) return v;
  }
  const mapping: Record<string, string> = {
    manual: labels[TransmissionType.MANUAL] || "Manuell",
    automatic: labels[TransmissionType.AUTOMATIC] || "Automat",
    semi_automatic: labels[TransmissionType.SEMI_AUTOMATIC] || "Halvautomat",
    cvt: labels[TransmissionType.CVT] || "CVT",
    other: labels[TransmissionType.OTHER] || "Annat",
  };
  return mapping[upper.toLowerCase()] ?? transmission;
}

/** Mappa drivmedel (t.ex. GASOLINE) till önskat språk. */
function formatFuelTypePdf(fuelType?: string, lang?: Language): string {
  const language: Language = lang ?? getLanguage();
  const ct = carTestLabels(language);
  if (!fuelType) return ct.pdf.values.notSpecifiedShort;
  const labels = fuelTypeLabels(language);
  const upper = fuelType.toUpperCase().replace(/-/g, "_");
  if (upper in VehicleFuelType) {
    const v = labels[upper as VehicleFuelType];
    if (v) return v;
  }
  const mapping: Record<string, string> = {
    gasoline: labels[VehicleFuelType.GASOLINE] || "Bensin",
    diesel: labels[VehicleFuelType.DIESEL] || "Diesel",
    electric: labels[VehicleFuelType.ELECTRIC] || "El",
    hybrid: labels[VehicleFuelType.HYBRID] || "Hybrid",
    plug_in_hybrid: labels[VehicleFuelType.PLUG_IN_HYBRID] || "Plug-in hybrid",
    ethanol: labels[VehicleFuelType.ETHANOL] || "Etanol",
    etanol: labels[VehicleFuelType.ETHANOL] || "Etanol",
    gas: labels[VehicleFuelType.GAS] || "Gas",
    other: labels[VehicleFuelType.OTHER] || "Annat",
  };
  return mapping[upper.toLowerCase()] ?? fuelType;
}

/** Mappa däcktyp (t.ex. "Vinter (dubbade)") till önskat språk. */
function formatTireTypePdf(tireType?: string, lang?: Language): string {
  const language: Language = lang ?? getLanguage();
  const ct = carTestLabels(language);
  if (!tireType) return ct.pdf.values.notSpecifiedShort;
  const vLabels = valuationLabels(language);
  const tireTypes = (vLabels.vehicleInfo.options?.tireType as Record<string, string>) || {};
  return tireTypes[tireType] ?? tireType;
}

/** Mappa fälgtyp (t.ex. "Originalfälgar", "Plåt") till önskat språk. */
function formatRimTypePdf(rimType?: string, lang?: Language): string {
  const language: Language = lang ?? getLanguage();
  const ct = carTestLabels(language);
  if (!rimType) return ct.pdf.values.notSpecifiedShort;
  const vLabels = valuationLabels(language);
  const rimTypes = (vLabels.vehicleInfo.options?.rimType as Record<string, string>) || {};
  return rimTypes[rimType] ?? rimType;
}

function getTestItemValue(
  key: string,
  data: { problem?: string; location?: string; source?: string; type?: string; issue?: string; area?: string; description?: string; other?: string } | undefined,
  lang?: string
): string {
  const language = lang || getLanguage();
  const ct = carTestLabels(language);
  if (!data) return ct.pdf.values.noProblems;
  // Note: key comparison should use the actual key names from the test protocol items
  // These are matched against the labels used in testProtocolItems array
  const crashKey = language === "en" ? "Crash" : "Krock";
  const acKey = ct.pdf.labels.ac;
  const windshieldKey = ct.pdf.labels.windshield;
  if (key === crashKey && data.problem === "unknown") return ct.pdf.values.unknown;
  if (key === acKey && data.problem === "not_tested") return ct.pdf.values.notTested;
  if (key === windshieldKey && data.problem === "stone_chip") return ct.pdf.values.stoneChip;
  if (data.problem !== "yes") return ct.pdf.values.noProblems;
  const rawReason =
    data.location || data.source || data.type || data.issue || data.area || data.description || ct.pdf.values.unknownReason;
  const reason = rawReason === ct.pdf.values.other && data.other ? data.other : rawReason;
  return `${ct.pdf.values.problem}: ${reason}`;
}

/** Ladda bild-URL till base64 (cookies skickas för autentiserade URL:er). */
async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: "cors", credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Bildformat från dataUrl för jsPDF.addImage. */
function getImageFormat(dataUrl: string): "JPEG" | "PNG" {
  return dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
}

/** Hämta bild som dataUrl + dimensioner (för aspect-ratio vid ritning). */
function getImageDimensions(
  dataUrl: string
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/** Beräkna w,h så att bilden får plats i maxW x maxH utan att stretchas. */
function fitInBox(
  imgW: number,
  imgH: number,
  maxW: number,
  maxH: number
): { w: number; h: number; x: number; y: number } {
  if (imgW <= 0 || imgH <= 0) return { w: maxW, h: maxH, x: 0, y: 0 };
  const scale = Math.min(maxW / imgW, maxH / imgH, 1);
  const w = imgW * scale;
  const h = imgH * scale;
  const x = (maxW - w) / 2;
  const y = (maxH - h) / 2;
  return { w, h, x, y };
}

/** Rita en grön check-ikon (cirkel + bock) som i DocumentView. */
function drawGreenCheckIcon(doc: jsPDF, x: number, y: number, r = 1.8) {
  doc.setFillColor(34, 197, 94);
  doc.circle(x + r, y - r, r, "F");
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.4);
  const s = r * 0.5;
  doc.line(x + r - s, y - r, x + r - 0.15, y - r + s - 0.15);
  doc.line(x + r - 0.15, y - r + s - 0.15, x + r + s + 0.2, y - r - s + 0.2);
  doc.setDrawColor(0, 0, 0);
}

/** Rita rött kryss för testprotokoll. */
function drawRedCrossIcon(doc: jsPDF, x: number, y: number, r = 1.8) {
  doc.setDrawColor(239, 68, 68);
  doc.setLineWidth(0.45);
  const d = r * 0.85;
  doc.line(x + r - d, y - r - d, x + r + d, y - r + d);
  doc.line(x + r + d, y - r - d, x + r - d, y - r + d);
  doc.setDrawColor(0, 0, 0);
}

export async function generateVehicleTestPdfPro(test: VehicleTest, lang?: Language): Promise<jsPDF> {
  const language: Language = lang ?? getLanguage();
  const ct = carTestLabels(language);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentTop = YELLOW_BAR_HEIGHT + 6;
  const contentBottom = pageHeight - MARGIN - FOOTER_HEIGHT;
  const rowCapacity = 3;

  let y = contentTop;
  let currentPage = 1;
  const genDateTime = (() => {
    const d = new Date();
    const locale = language === "en" ? "en-US" : "sv-SE";
    const dateStr = d.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timeStr = d.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr}, ${timeStr}`;
  })();

  const drawHeader = () => {
    pdf.setFillColor(251, 191, 36);
    pdf.rect(0, 0, pageWidth, YELLOW_BAR_HEIGHT, "F");
    const headerTextY = YELLOW_BAR_HEIGHT - YELLOW_BAR_PADDING_BOTTOM;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(0, 0, 0);
    pdf.text(ct.pdf.title, MARGIN, headerTextY);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);
    pdf.text(genDateTime, pageWidth - MARGIN, headerTextY, { align: "right" });
  };

  const drawFooter = (pageNum: number) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    pdf.text(`${ct.pdf.page} ${pageNum}`, pageWidth / 2, pageHeight - 6, { align: "center" });
  };

  const newPage = () => {
    pdf.addPage();
    currentPage++;
    y = contentTop;
    // Header (BILENIA + datum) endast på första sidan
    if (currentPage === 1) drawHeader();
  };

  const ensureSpace = (needed: number) => {
    if (y + needed > contentBottom) newPage();
  };

  let cardStartY = 0;
  let leftX = MARGIN;
  const colWidthDynamic = () => (pageWidth - leftX - MARGIN - 10) / 2;

  const cardStart = () => {
    cardStartY = y;
    y += CARD_PADDING;
    leftX = MARGIN + CARD_PADDING;
  };
  const cardEnd = () => {
    const h = y - cardStartY + CARD_PADDING_BOTTOM;
    y = cardStartY + h + CARD_GAP;
    leftX = MARGIN;
  };

  const sectionTitle = (title: string) => {
    ensureSpace(LINE_HEIGHT * 2);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text(title, leftX, y);
    y += LINE_HEIGHT + 1;
  };

  const subSectionTitle = (title: string, gapAfter = LINE_HEIGHT + SUB_SECTION_GAP) => {
    ensureSpace(LINE_HEIGHT + 1);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(title, leftX, y);
    y += gapAfter;
  };

  /** Etikett utgråad, värde fet – som i DocumentView. */
  const kv = (key: string, value: string, x?: number) => {
    const x0 = x ?? leftX;
    const cw = colWidthDynamic();
    if (y > contentBottom - LINE_HEIGHT) newPage();
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${key}:`, x0, y);
    const kw = pdf.getTextWidth(`${key}: `);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    const valLines = pdf.splitTextToSize(value || "—", cw - kw - 2);
    pdf.text(valLines[0], x0 + kw, y);
    y += LINE_HEIGHT;
    for (let i = 1; i < valLines.length; i++) {
      if (y > contentBottom - LINE_HEIGHT) newPage();
      pdf.text(valLines[i], x0 + kw, y);
      y += LINE_HEIGHT;
    }
  };

  const cw3 = () => (pageWidth - leftX - MARGIN - 12) / 3;
  const colGap3 = 6;
  const kvRow = (k1: string, v1: string, k2: string, v2: string, startX?: number) => {
    const cw = colWidthDynamic();
    const x0 = startX ?? leftX;
    ensureSpace(LINE_HEIGHT + 2);
    const v1Str = (v1 || "—").slice(0, 35);
    const v2Str = (v2 || "—").slice(0, 35);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k1}:`, x0, y);
    const kw1 = pdf.getTextWidth(`${k1}: `);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text(v1Str, x0 + kw1, y);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k2}:`, x0 + cw + 10, y);
    const kw2 = pdf.getTextWidth(`${k2}: `);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text(v2Str, x0 + cw + 10 + kw2, y);
    y += LINE_HEIGHT;
  };

  const kvRow3 = (k1: string, v1: string, k2: string, v2: string, k3: string, v3: string) => {
    const w = cw3();
    ensureSpace(LINE_HEIGHT + 2);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k1}:`, leftX, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text((v1 || "—").slice(0, 25), leftX + pdf.getTextWidth(`${k1}: `), y);
    const x2 = leftX + w + colGap3;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k2}:`, x2, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text((v2 || "—").slice(0, 25), x2 + pdf.getTextWidth(`${k2}: `), y);
    const x3 = leftX + (w + colGap3) * 2;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k3}:`, x3, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text((v3 || "—").slice(0, 25), x3 + pdf.getTextWidth(`${k3}: `), y);
    y += LINE_HEIGHT;
  };

  /** Gap mellan etikett och värde (rubrik + value), ca 1 px extra för läsbarhet. */
  const labelValueGap = 4.5;
  /** Tre kolumner: rad 1 = etiketter (grå), rad 2 = värden (svart fet). Utan bullet/ikon. */
  const labelValueRow3 = (l1: string, v1: string, l2: string, v2: string, l3: string, v3: string) => {
    const w = cw3();
    ensureSpace(LINE_HEIGHT + labelValueGap + 0.5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${l1}:`, leftX, y);
    pdf.text(`${l2}:`, leftX + w + colGap3, y);
    pdf.text(`${l3}:`, leftX + (w + colGap3) * 2, y);
    y += labelValueGap;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text((v1 || "—").slice(0, 28), leftX, y);
    pdf.text((v2 || "—").slice(0, 28), leftX + w + colGap3, y);
    pdf.text((v3 || "—").slice(0, 28), leftX + (w + colGap3) * 2, y);
    y += LINE_HEIGHT + 0.5;
  };

  /** Två kolumner: etiketter (grå) ovanför värden (svart fet). */
  const labelValueRow2 = (l1: string, v1: string, l2: string, v2: string) => {
    const w = cw3();
    ensureSpace(LINE_HEIGHT + labelValueGap + 0.5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${l1}:`, leftX, y);
    pdf.text(`${l2}:`, leftX + w + colGap3, y);
    y += labelValueGap;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text((v1 || "—").slice(0, 28), leftX, y);
    pdf.text((v2 || "—").slice(0, 28), leftX + w + colGap3, y);
    y += LINE_HEIGHT + 0.5;
  };

  /** Två kolumner i samma position som kolumn 1 och 2 i 3-kolumnslayout (undviker tom tredje kolumn). */
  const kvRow2In3 = (k1: string, v1: string, k2: string, v2: string) => {
    const w = cw3();
    ensureSpace(LINE_HEIGHT + 2);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k1}:`, leftX, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text((v1 || "—").slice(0, 25), leftX + pdf.getTextWidth(`${k1}: `), y);
    const x2 = leftX + w + colGap3;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${k2}:`, x2, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text((v2 || "—").slice(0, 25), x2 + pdf.getTextWidth(`${k2}: `), y);
    y += LINE_HEIGHT;
  };

  // —— Status + Reg nr (finare: endast värden fetstil, samma kort)
  const statusLabels: Record<string, string> = {
    approved: ct.pdf.status.approved,
    rejected: ct.pdf.status.rejected,
    complement: ct.pdf.status.complement,
    "pending-review": ct.pdf.status.pendingReview,
    ongoing: ct.pdf.status.ongoing,
    created: ct.pdf.status.created,
    completed: ct.pdf.status.completed,
    "active-listing": ct.pdf.status.activeListing,
  };
  const testStatusStr = statusLabels[test.status] || test.status;
  const rejectionReason = (test as { rejectionReason?: string }).rejectionReason;
  const regNr = test.vehicleDetails?.regNumber || test.regNumber;
  cardStart();
  ensureSpace(LINE_HEIGHT + 1);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(100, 100, 100);
  let statusRegX = leftX;
  if (regNr) {
    pdf.text(`${ct.pdf.labels.regNr}:`, statusRegX, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    const regLabelW = pdf.getTextWidth(`${ct.pdf.labels.regNr}: `);
    pdf.text(regNr, statusRegX + regLabelW, y);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    statusRegX = statusRegX + regLabelW + pdf.getTextWidth(regNr) + 10;
  }
  pdf.text(`${ct.pdf.labels.status}:`, statusRegX, y);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(testStatusStr, statusRegX + pdf.getTextWidth(`${ct.pdf.labels.status}: `), y);
  y += LINE_HEIGHT;
  if (test.status === "rejected" && rejectionReason) {
    y += 1;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);
    const reasonLines = pdf.splitTextToSize(`${ct.pdf.labels.rejectionReason}: ${rejectionReason}`, pageWidth - leftX - MARGIN);
    for (const line of reasonLines) {
      pdf.text(line, leftX, y);
      y += LINE_HEIGHT;
    }
  }
  y += 1;
  cardEnd();

  const priceStr =
    typeof test.price === "string"
      ? test.price
      : (test.price as { desired?: string; estimated?: string })?.desired ||
      (test.price as { desired?: string; estimated?: string })?.estimated ||
      ct.pdf.values.notSpecified;
  const vd: Partial<VehicleDetails> = test.vehicleDetails || {};
  const vtest = test.vehicleTestData;
  const merge = (a: unknown, b: unknown) => (b !== undefined && b !== null ? b : a);

  const imgMaxW = 54;
  const imgMaxH = 40;
  const imgSpacing = 4;
  const imgLabelH = 5;

  /** Rita en bild i en ruta med aspect-ratio bevarad; optional ram runt Huvudbild. */
  const addImageInBox = async (
    x: number,
    y0: number,
    url: string | undefined,
    label: string,
    isThumbnail: boolean
  ): Promise<number> => {
    const boxH = imgMaxH + imgLabelH + 2;
    if (!url || !(url.startsWith("http") || url.startsWith("data:"))) {
      pdf.setDrawColor(200, 200, 200);
      pdf.setFillColor(248, 248, 248);
      pdf.rect(x, y0, imgMaxW, imgMaxH, "FD");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(ct.pdf.labels.image, x + imgMaxW / 2 - 4, y0 + imgMaxH / 2);
      if (label) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(80, 80, 80);
        pdf.text(label, x, y0 + imgMaxH + 3);
      }
      return boxH;
    }
    const dataUrl: string | null = url.startsWith("data:") ? url : await fetchImageAsDataUrl(url);
    if (!dataUrl) {
      pdf.setDrawColor(200, 200, 200);
      pdf.setFillColor(248, 248, 248);
      pdf.rect(x, y0, imgMaxW, imgMaxH, "FD");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(ct.pdf.labels.image, x + imgMaxW / 2 - 4, y0 + imgMaxH / 2);
      if (label) {
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(80, 80, 80);
        pdf.text(label, x, y0 + imgMaxH + 3);
      }
      return boxH;
    }
    const dims = await getImageDimensions(dataUrl);
    const imgW = dims ? dims.width : imgMaxW;
    const imgH = dims ? dims.height : imgMaxH;
    const { w, h, x: dx, y: dy } = fitInBox(imgW, imgH, imgMaxW, imgMaxH);
    const px = x + dx;
    const py = y0 + dy;
    const format = getImageFormat(dataUrl);
    try {
      pdf.addImage(dataUrl, format, px, py, w, h);
    } catch {
      pdf.setDrawColor(200, 200, 200);
      pdf.setFillColor(248, 248, 248);
      pdf.rect(x, y0, imgMaxW, imgMaxH, "FD");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(ct.pdf.labels.image, x + imgMaxW / 2 - 4, y0 + imgMaxH / 2);
    }
    if (label) {
      pdf.setFont("helvetica", isThumbnail ? "bold" : "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.text(label, x, y0 + imgMaxH + 3);
      pdf.setTextColor(0, 0, 0);
      return boxH;
    }
    pdf.setTextColor(0, 0, 0);
    return imgMaxH + 2;
  };

  // —— Kort: Säljarinformation
  cardStart();
  sectionTitle(ct.pdf.labels.sellerInfo);
  kvRow(ct.pdf.labels.name, test.customer?.username || ct.pdf.values.notSpecified, ct.pdf.labels.email, test.customer?.email || ct.pdf.values.notSpecified);
  kvRow(ct.pdf.labels.phone, test.customer?.phoneNumber || ct.pdf.values.notSpecified, ct.pdf.labels.customerType, formatCustomerTypePdf(test.customer?.role, language as Language));
  cardEnd();

  // —— Kort: Önskat pris (ÖNSKAT PRIS och pris samma vänsterjustering)
  cardStart();
  ensureSpace(8);
  const priceY = y;
  const priceFontSize = 16;
  const baselineY = priceY + 2 + priceFontSize * 0.35;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.text(ct.pdf.labels.desiredPrice, leftX, baselineY);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(priceFontSize);
  pdf.setTextColor(0, 0, 0);
  const labelW = pdf.getTextWidth(ct.pdf.labels.desiredPrice);
  pdf.text(priceStr, leftX + labelW + 4, baselineY);
  const momsStr = `${ct.pdf.labels.vat} ${test.vatCar ? ct.pdf.labels.vatIncluded : ct.pdf.labels.vatExcluded}`;
  const priceW = pdf.getTextWidth(priceStr);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.text(momsStr, leftX + labelW + 4 + priceW + 3, baselineY);
  y = priceY + 11;
  cardEnd();

  // —— Kort: Bilder (endast Exteriör + Interiör; Huvudbild markerad; exkludera 360-ramar)
  const extArr = Array.isArray(test.images?.exterior)
    ? test.images.exterior.filter((img: ExteriorImage) => !is360ExteriorFrame(img))
    : [];
  const intArr = Array.isArray(test.images?.interior) ? test.images.interior : [];
  if (extArr.some((i: ExteriorImage) => i.url) || intArr.some((i: FileObject) => i.url)) {
    cardStart();
    sectionTitle(ct.pdf.labels.images);
    const extWithUrl = extArr.filter((img: ExteriorImage) => img.url);
    const intWithUrl = intArr.filter((img: FileObject) => img.url);
    if (extWithUrl.length) {
      subSectionTitle(ct.pdf.labels.exterior, 1.5);
      for (let i = 0; i < extWithUrl.length; i += rowCapacity) {
        const row = extWithUrl.slice(i, i + rowCapacity);
        ensureSpace(imgMaxH + imgLabelH + 6);
        let maxRowH = 0;
        for (let j = 0; j < row.length; j++) {
          const xx = leftX + j * (imgMaxW + imgSpacing);
          const img = row[j] as ExteriorImage;
          const isThumb = !!(img as ExteriorImage).isThumbnail;
          const indexInSection = i + j;
          const label = isThumb ? ct.pdf.labels.thumbnail : String(indexInSection + 1);
          const h = await addImageInBox(xx, y, img.url, label, isThumb);
          if (h > maxRowH) maxRowH = h;
        }
        y += maxRowH + 0.5;
      }
      y += 2;
    }
    if (intWithUrl.length) {
      subSectionTitle(ct.pdf.labels.interior, 1.5);
      for (let i = 0; i < intWithUrl.length; i += rowCapacity) {
        const row = intWithUrl.slice(i, i + rowCapacity);
        ensureSpace(imgMaxH + imgLabelH + 6);
        let maxRowH = 0;
        for (let j = 0; j < row.length; j++) {
          const xx = leftX + j * (imgMaxW + imgSpacing);
          const img = row[j] as FileObject;
          const indexInSection = i + j;
          const label = String(indexInSection + 1);
          const h = await addImageInBox(xx, y, img.url, label, false);
          if (h > maxRowH) maxRowH = h;
        }
        y += maxRowH + 1;
      }
    }
    cardEnd();
  }

  // —— Kort: Fordonsinformation (etikett rad grå, värde rad svart fet – utan bullet/ikon)
  cardStart();
  sectionTitle(ct.pdf.labels.vehicleInfo);
  labelValueRow3(ct.pdf.labels.regNr, vd.regNumber || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.vin, (merge(vd.vin, test.vin) as string) || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.model, vd.model || ct.pdf.values.notSpecifiedShort);
  labelValueRow3(ct.pdf.labels.variant, vd.variant || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.year, vd.year?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.transmission, formatTransmissionPdf(vd.transmission, language as Language));
  labelValueRow3(ct.pdf.labels.mileage, `${formatMileage(vd.mileage) || "—"} ${ct.pdf.values.miles}`, ct.pdf.labels.fuel, formatFuelTypePdf(vd.fuelType, language as Language), ct.pdf.labels.color, vd.color || ct.pdf.values.notSpecifiedShort);
  labelValueRow2(ct.pdf.labels.seats, vd.seats?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.bodyType, vd.bodyType || ct.pdf.values.notSpecifiedShort);
  cardEnd();

  // —— Kort: Ytterligare information (samma stil: etikett grå, värde svart fet)
  cardStart();
  sectionTitle(ct.pdf.labels.additionalInfo);
  const locale = language === "en" ? "en-US" : "sv-SE";
  labelValueRow3(ct.pdf.labels.owners, vd.owners?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.keys, vd.keys?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.origin, vd.origin || ct.pdf.values.notSpecifiedShort);
  labelValueRow3(ct.pdf.labels.annualTax, vd.annualVehicleTax != null ? `${Number(vd.annualVehicleTax).toLocaleString(locale)} ${ct.pdf.values.kr}` : ct.pdf.values.notSpecifiedShort, ct.pdf.labels.engine, vd.engine || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.horsepower, vd.horsepower?.toString() || ct.pdf.values.notSpecifiedShort);
  labelValueRow3(ct.pdf.labels.engineVolume, vd.engineVolume?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.emissionClass, vd.emissionClass || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.accidentVehicle, vtest?.crash?.problem === "yes" ? ct.pdf.values.yes : vtest?.crash?.problem === "unknown" ? ct.pdf.values.unknown : ct.pdf.values.no);
  labelValueRow2(ct.pdf.labels.doors, vd.doors?.toString() || ct.pdf.values.notSpecifiedShort, ct.pdf.labels.upholstery, vd.upholstery || ct.pdf.values.notSpecifiedShort);
  if (vd.fuelType === "El" || vd.fuelType === "Elbil" || vd.fuelType === "ELECTRIC") {
    ensureSpace(LINE_HEIGHT * 2 + 1);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`${ct.pdf.labels.electricRange}:`, leftX, y);
    y += LINE_HEIGHT;
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text(vd.electricDriveRange ? `${vd.electricDriveRange} ${ct.pdf.values.km}` : ct.pdf.values.notSpecifiedShort, leftX, y);
    y += LINE_HEIGHT + 1;
  }
  cardEnd();

  // —— Kort: Utrustning i 3 kolumner (grön check som i DocumentView)
  const equipment = vd.equipment;
  const iconW = 4.5;
  const eqColGap = 6;
  const eqTotalW = pageWidth - leftX - MARGIN;
  const eqSingleColTotal = (eqTotalW - 2 * eqColGap) / 3;
  const eqColW = eqSingleColTotal - iconW - 2;
  const eqCol2X = leftX + eqSingleColTotal + eqColGap;
  const eqCol3X = leftX + 2 * (eqSingleColTotal + eqColGap);
  if (equipment && equipment.length > 0) {
    cardStart();
    sectionTitle(ct.pdf.labels.equipment);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    for (let i = 0; i < equipment.length; i += 3) {
      const item0 = getEquipmentLabel(equipment[i], language);
      const item1 = equipment[i + 1] ? getEquipmentLabel(equipment[i + 1], language) : undefined;
      const item2 = equipment[i + 2] ? getEquipmentLabel(equipment[i + 2], language) : undefined;
      const lines0 = pdf.splitTextToSize(item0, eqColW);
      const lines1 = item1 ? pdf.splitTextToSize(item1, eqColW) : [];
      const lines2 = item2 ? pdf.splitTextToSize(item2, eqColW) : [];
      const h0 = lines0.length * LINE_HEIGHT;
      const h1 = lines1.length * LINE_HEIGHT;
      const h2 = lines2.length * LINE_HEIGHT;
      const rowH = Math.max(h0, h1, h2) + 1;
      ensureSpace(rowH);
      const rowStartY = y;
      let drawnH0 = LINE_HEIGHT;
      drawGreenCheckIcon(pdf, leftX, y + 1.2, 1.8);
      pdf.setTextColor(0, 0, 0);
      pdf.text(lines0[0], leftX + iconW, y);
      for (let j = 1; j < lines0.length; j++) {
        y += LINE_HEIGHT;
        pdf.text(lines0[j], leftX + iconW, y);
        drawnH0 += LINE_HEIGHT;
      }
      let drawnH1 = LINE_HEIGHT;
      if (item1) {
        y = rowStartY;
        drawGreenCheckIcon(pdf, eqCol2X, y + 1.2, 1.8);
        pdf.text(lines1[0], eqCol2X + iconW, y);
        for (let j = 1; j < lines1.length; j++) {
          y += LINE_HEIGHT;
          pdf.text(lines1[j], eqCol2X + iconW, y);
          drawnH1 += LINE_HEIGHT;
        }
      }
      let drawnH2 = LINE_HEIGHT;
      if (item2) {
        y = rowStartY;
        drawGreenCheckIcon(pdf, eqCol3X, y + 1.2, 1.8);
        pdf.text(lines2[0], eqCol3X + iconW, y);
        for (let j = 1; j < lines2.length; j++) {
          y += LINE_HEIGHT;
          pdf.text(lines2[j], eqCol3X + iconW, y);
          drawnH2 += LINE_HEIGHT;
        }
      }
      y = rowStartY + Math.max(drawnH0, drawnH1, drawnH2) + 1;
    }
    cardEnd();
  }

  // —— Kort: Hjul – som DocView/TiresSection: Primär/Sekundär + bilder, sedan ett kort med tre kolumner (Hjulinformation | Bromseffektivitet | Mönsterdjup)
  const prim = Array.isArray(test.images?.tires?.primary) ? test.images.tires.primary : [];
  const sec = Array.isArray(test.images?.tires?.secondary) ? test.images.tires.secondary : [];
  const primTires = prim.filter((t: FileObject) => t.url);
  const secTires = sec.filter((t: FileObject) => t.url);
  cardStart();
  sectionTitle(ct.pdf.labels.wheels);

  subSectionTitle(ct.pdf.labels.primaryTireSet);
  if (primTires.length === 0) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(ct.pdf.labels.noPrimaryTireImages, leftX, y);
    y += LINE_HEIGHT + 2;
  } else {
    for (let i = 0; i < primTires.length; i += rowCapacity) {
      const row = primTires.slice(i, i + rowCapacity);
      ensureSpace(imgMaxH + imgLabelH + 6);
      let maxRowH = 0;
        for (let j = 0; j < row.length; j++) {
          const xx = leftX + j * (imgMaxW + imgSpacing);
          const h = await addImageInBox(xx, y, row[j].url, "", false);
          if (h > maxRowH) maxRowH = h;
        }
      y += maxRowH + 0.5;
    }
    y += 0.5;
  }

  const hjulBlockPad = 4;
  const hjulInnerPad = 3;
  const hjulColGap = colGap3;
  const hjulTitleH = LINE_HEIGHT + 2;
  const hjulTriBlockH =
    hjulBlockPad * 2 + hjulTitleH + LINE_HEIGHT * 2 + 2;
  const hjulBlockR = 3.5;
  const hjulBlockW = pageWidth - leftX - MARGIN + 1;
  const hjulContentX = leftX + hjulInnerPad;

  const drawHjulColumnDividersPdf = (
    divTopY: number,
    divBotY: number,
    col2Start: number,
    col3Start: number,
  ) => {
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.15);
    const mid12 = col2Start - hjulColGap * 0.45;
    const mid23 = col3Start - hjulColGap * 0.45;
    pdf.line(mid12, divTopY, mid12, divBotY);
    pdf.line(mid23, divTopY, mid23, divBotY);
  };

  const drawHjulCellLabelValuePdf = (
    colLeft: number,
    colRight: number,
    label: string,
    value: string,
    baselineY: number,
  ) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    const lab = `${label}:`;
    pdf.text(lab, colLeft, baselineY);
    const afterLab = colLeft + pdf.getTextWidth(`${lab} `);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    const raw = String(value ?? "").trim();
    const v =
      raw.length > 34
        ? `${raw.slice(0, 31)}…`
        : raw || ct.pdf.values.notSpecifiedShort;
    const vw = pdf.getTextWidth(v);
    const valX = Math.max(afterLab, colRight - vw);
    pdf.text(v, valX, baselineY);
  };

  const notSpecShort = ct.pdf.values.notSpecifiedShort;
  const strTrim = (v: unknown) =>
    v != null && v !== "" ? String(v).trim() : "";
  const brakeF = strTrim(vtest?.brakes?.frontEffectiveness) || notSpecShort;
  const brakeR = strTrim(vtest?.brakes?.rearEffectiveness) || notSpecShort;
  const primaryTreadF = strTrim(vtest?.tires?.primary?.frontDepth) || notSpecShort;
  const primaryTreadR = strTrim(vtest?.tires?.primary?.rearDepth) || notSpecShort;

  const usableInner = hjulBlockW - hjulInnerPad * 2;
  const hjulColW = (usableInner - 2 * hjulColGap) / 3;
  const hjulC1 = hjulContentX;
  const hjulC2 = hjulC1 + hjulColW + hjulColGap;
  const hjulC3 = hjulC2 + hjulColW + hjulColGap;
  const hjulR1 = hjulC1 + hjulColW;
  const hjulR2 = hjulC2 + hjulColW;
  const hjulR3 = hjulC3 + hjulColW;

  ensureSpace(hjulTriBlockH);
  const primaryBlockTop = y;
  pdf.setFillColor(249, 249, 249);
  pdf.roundedRect(
    leftX - 0.5,
    primaryBlockTop - 0.5,
    hjulBlockW,
    hjulTriBlockH,
    hjulBlockR,
    hjulBlockR,
    "F",
  );

  let hy = primaryBlockTop + hjulBlockPad;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text(ct.pdf.labels.wheelInfo, hjulC1, hy);
  pdf.text(ct.pdf.labels.brakeEffectiveness, hjulC2, hy);
  pdf.text(ct.pdf.labels.treadDepth, hjulC3, hy);
  hy += hjulTitleH;

  drawHjulCellLabelValuePdf(
    hjulC1,
    hjulR1,
    ct.pdf.labels.tireType,
    formatTireTypePdf(vd.currentTireType, language),
    hy,
  );
  drawHjulCellLabelValuePdf(hjulC2, hjulR2, ct.pdf.labels.front, brakeF, hy);
  drawHjulCellLabelValuePdf(
    hjulC3,
    hjulR3,
    ct.pdf.labels.front,
    primaryTreadF,
    hy,
  );
  hy += LINE_HEIGHT;

  drawHjulCellLabelValuePdf(
    hjulC1,
    hjulR1,
    ct.pdf.labels.rimType,
    formatRimTypePdf(vd.currentRimType, language),
    hy,
  );
  drawHjulCellLabelValuePdf(hjulC2, hjulR2, ct.pdf.labels.rear, brakeR, hy);
  drawHjulCellLabelValuePdf(
    hjulC3,
    hjulR3,
    ct.pdf.labels.rear,
    primaryTreadR,
    hy,
  );

  drawHjulColumnDividersPdf(
    primaryBlockTop + hjulBlockPad + 1,
    primaryBlockTop + hjulTriBlockH - hjulBlockPad - 1,
    hjulC2,
    hjulC3,
  );

  y = primaryBlockTop + hjulTriBlockH + 2;

  if (secTires.length > 0 || vd.extraTireSet) {
    y += 2;
    subSectionTitle(ct.pdf.labels.secondaryTireSet);
    if (vd.extraTireSet) {
      const normTreadDepth = (d?: string | null) => {
        if (!d || d === "null") return notSpecShort;
        const t = String(d).trim();
        return t || notSpecShort;
      };
      const s = vtest?.tires?.secondary;
      const secondaryTreadF = normTreadDepth(s?.frontDepth);
      const secondaryTreadR = normTreadDepth(s?.rearDepth);

      ensureSpace(hjulTriBlockH);
      const secBlockTop = y;
      pdf.setFillColor(249, 249, 249);
      pdf.roundedRect(
        leftX - 0.5,
        secBlockTop - 0.5,
        hjulBlockW,
        hjulTriBlockH,
        hjulBlockR,
        hjulBlockR,
        "F",
      );

      let sy = secBlockTop + hjulBlockPad;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(ct.pdf.labels.wheelInfo, hjulC1, sy);
      pdf.text(ct.pdf.labels.brakeEffectiveness, hjulC2, sy);
      pdf.text(ct.pdf.labels.treadDepth, hjulC3, sy);
      sy += hjulTitleH;

      drawHjulCellLabelValuePdf(
        hjulC1,
        hjulR1,
        ct.pdf.labels.extraTireType,
        formatTireTypePdf(vd.extraTireType, language),
        sy,
      );
      drawHjulCellLabelValuePdf(hjulC2, hjulR2, ct.pdf.labels.front, brakeF, sy);
      drawHjulCellLabelValuePdf(
        hjulC3,
        hjulR3,
        ct.pdf.labels.front,
        secondaryTreadF,
        sy,
      );
      sy += LINE_HEIGHT;

      drawHjulCellLabelValuePdf(
        hjulC1,
        hjulR1,
        ct.pdf.labels.extraRimType,
        formatRimTypePdf(vd.extraRimType, language),
        sy,
      );
      drawHjulCellLabelValuePdf(hjulC2, hjulR2, ct.pdf.labels.rear, brakeR, sy);
      drawHjulCellLabelValuePdf(
        hjulC3,
        hjulR3,
        ct.pdf.labels.rear,
        secondaryTreadR,
        sy,
      );

      drawHjulColumnDividersPdf(
        secBlockTop + hjulBlockPad + 1,
        secBlockTop + hjulTriBlockH - hjulBlockPad - 1,
        hjulC2,
        hjulC3,
      );

      y = secBlockTop + hjulTriBlockH + 2;
    }
    if (secTires.length) {
      y += 2;
      for (let i = 0; i < secTires.length; i += rowCapacity) {
        const row = secTires.slice(i, i + rowCapacity);
        ensureSpace(imgMaxH + imgLabelH + 6);
        let maxRowH = 0;
        for (let j = 0; j < row.length; j++) {
          const xx = leftX + j * (imgMaxW + imgSpacing);
          const h = await addImageInBox(xx, y, row[j].url, "", false);
          if (h > maxRowH) maxRowH = h;
        }
        y += maxRowH + 1;
      }
    }
  }
  cardEnd();

  // —— Kort: Skador (respektive skadekommentar under respektive bild)
  const dmgArr = Array.isArray(test.images?.damage) ? test.images.damage : [];
  const dmgWithUrl = dmgArr.filter((d: DamageImage) => d.url);
  if (dmgWithUrl.length > 0) {
    cardStart();
    sectionTitle(ct.pdf.labels.damages);
    const commentMaxW = imgMaxW;
    for (let i = 0; i < dmgWithUrl.length; i += rowCapacity) {
      const row = dmgWithUrl.slice(i, i + rowCapacity) as DamageImage[];
      ensureSpace(imgMaxH + 16);
      const imgHeights: number[] = [];
      for (let j = 0; j < row.length; j++) {
        const xx = leftX + j * (imgMaxW + imgSpacing);
        const h = await addImageInBox(xx, y, row[j].url, "", false);
        imgHeights.push(h);
      }
      let maxCellH = 0;
      for (let j = 0; j < row.length; j++) {
        const d = row[j];
        const xx = leftX + j * (imgMaxW + imgSpacing);
        let cellH = imgHeights[j];
        if (d.comment) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(80, 80, 80);
          const lines = pdf.splitTextToSize(d.comment, commentMaxW);
          let cy = y + imgHeights[j] + 1;
          for (const line of lines) {
            pdf.text(line, xx, cy);
            cy += LINE_HEIGHT;
          }
          cellH += lines.length * LINE_HEIGHT + 1;
        }
        if (cellH > maxCellH) maxCellH = cellH;
      }
      y += maxCellH + 2;
    }
    cardEnd();
  }

  // —— Kort: Service (alltid synlig; servicedetaljer + servicebilder eller placeholder)
  const svcArr = Array.isArray(test.images?.serviceImages) ? test.images.serviceImages : [];
  const svcDetails = test.images?.serviceDetails?.serviceDetails;
  const svcWithUrl = svcArr.filter((s: FileObject) => s.url);
  const hasSvcContent = (svcDetails?.length ?? 0) > 0 || svcWithUrl.length > 0;

  cardStart();
  sectionTitle(ct.pdf.labels.serviceHistory);
  const svcSubTitleStyle = () => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
  };
  if (svcDetails?.length) {
    ensureSpace(LINE_HEIGHT + 2);
    svcSubTitleStyle();
    pdf.text(ct.pdf.labels.serviceDetails, leftX, y);
    y += LINE_HEIGHT + 1;
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    for (const entry of svcDetails) {
      const raw = entry.date instanceof Date ? entry.date.toISOString() : String(entry.date);
      const dateStr = raw.includes("T") ? raw.split("T")[0] : raw;
      const line = `${dateStr} · ${entry.mileage ?? "—"} ${ct.pdf.values.miles}${entry.serviceType ? ` · ${entry.serviceType}` : ""}${entry.notes ? ` · ${entry.notes}` : ""}`;
      const lines = pdf.splitTextToSize(line, pageWidth - leftX - MARGIN);
      for (const l of lines) {
        ensureSpace(LINE_HEIGHT);
        pdf.setFont("helvetica", "normal");
        pdf.text(l, leftX, y);
        y += LINE_HEIGHT;
      }
    }
    y += 1;
  }
  if (svcWithUrl.length) {
    ensureSpace(LINE_HEIGHT + 1);
    svcSubTitleStyle();
    pdf.text(ct.pdf.labels.serviceImages, leftX, y);
    y += 2;
    for (let i = 0; i < svcWithUrl.length; i += rowCapacity) {
      const row = svcWithUrl.slice(i, i + rowCapacity);
      ensureSpace(imgMaxH + imgLabelH + 6);
      let maxRowH = 0;
      for (let j = 0; j < row.length; j++) {
        const xx = leftX + j * (imgMaxW + imgSpacing);
        const h = await addImageInBox(xx, y, row[j].url, "", false);
        if (h > maxRowH) maxRowH = h;
      }
      y += maxRowH + 1;
    }
  }
  if (!hasSvcContent) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    pdf.text(ct.pdf.labels.noServiceInfo, leftX, y);
    y += LINE_HEIGHT;
  }
  cardEnd();

  // —— Kort: Testprotokoll med gröna/röda ikoner som i DocumentView
  const getTestStatus = (
    key: string,
    data: { problem?: string } | undefined
  ): "ok" | "error" => {
    if (!data) return "ok";
    const acLabel = ct.pdf.labels.ac;
    const windshieldLabel = ct.pdf.labels.windshield;
    if (key === acLabel && (data.problem === "yes" || data.problem === "not_tested")) return "error";
    if (key === windshieldLabel && (data.problem === "yes" || data.problem === "stone_chip")) return "error";
    if (data.problem === "yes") return "error";
    return "ok";
  };
  const testProtocolItems: { name: string; desc: string; status: "ok" | "error" }[] = [
    { name: ct.pdf.labels.testSpeed, desc: vtest?.speed ? `${vtest.speed} ${ct.pdf.values.kmh}` : ct.pdf.values.notSpecified, status: vtest?.speed ? "ok" : "error" },
    { name: ct.pdf.labels.vibrations, desc: getTestItemValue(ct.pdf.labels.vibrations, vtest?.vibrations, language), status: getTestStatus(ct.pdf.labels.vibrations, vtest?.vibrations) },
    { name: ct.pdf.labels.leaks, desc: getTestItemValue(ct.pdf.labels.leaks, vtest?.leaks, language), status: getTestStatus(ct.pdf.labels.leaks, vtest?.leaks) },
    { name: ct.pdf.labels.sounds, desc: getTestItemValue(ct.pdf.labels.sounds, vtest?.noises, language), status: getTestStatus(ct.pdf.labels.sounds, vtest?.noises) },
    { name: ct.pdf.labels.gearbox, desc: getTestItemValue(ct.pdf.labels.gearbox, vtest?.gearbox, language), status: getTestStatus(ct.pdf.labels.gearbox, vtest?.gearbox) },
    { name: ct.pdf.labels.ac, desc: getTestItemValue(ct.pdf.labels.ac, vtest?.ac, language), status: getTestStatus(ct.pdf.labels.ac, vtest?.ac) },
    { name: ct.pdf.labels.dashboard, desc: getTestItemValue(ct.pdf.labels.dashboard, vtest?.dashboard, language), status: getTestStatus(ct.pdf.labels.dashboard, vtest?.dashboard) },
    { name: ct.pdf.labels.windshield, desc: getTestItemValue(ct.pdf.labels.windshield, vtest?.windshield, language), status: getTestStatus(ct.pdf.labels.windshield, vtest?.windshield) },
  ];
  const TEST_PROTOCOL_ITEM_GAP = 2;
  cardStart();
  sectionTitle(ct.pdf.labels.testProtocol);
  const iconX = pageWidth - MARGIN - 5;
  for (const item of testProtocolItems) {
    ensureSpace(LINE_HEIGHT * 2.5 + TEST_PROTOCOL_ITEM_GAP);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(item.name, leftX, y);
    if (item.status === "ok") drawGreenCheckIcon(pdf, iconX - 2, y + 1.2, 1.6);
    else drawRedCrossIcon(pdf, iconX - 2, y + 1.2, 1.6);
    y += LINE_HEIGHT + 0.5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    const descLines = pdf.splitTextToSize(item.desc || "—", iconX - leftX - 6);
    for (const line of descLines) {
      if (y > contentBottom - LINE_HEIGHT) newPage();
      pdf.text(line, leftX, y);
      y += LINE_HEIGHT;
    }
    y += TEST_PROTOCOL_ITEM_GAP;
  }
  cardEnd();

  // —— Kort: Kommentarer
  if (test.comments) {
    cardStart();
    sectionTitle(ct.pdf.labels.comments);
    const lines = pdf.splitTextToSize(test.comments, pageWidth - leftX - MARGIN);
    for (const line of lines) {
      ensureSpace(LINE_HEIGHT);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(line, leftX, y);
      y += LINE_HEIGHT;
    }
    cardEnd();
  }

  // Sidfot på alla sidor
  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    drawFooter(p);
  }

  return pdf;
}
